import { Component } from '@angular/core';
import { BasicList } from '../../../classes';
import { UserDataApi, LoopBackConfig, UserData, LoopBackAuth } from '../../../../assets/js/lb-sdk';
import { BsModalService } from 'ngx-bootstrap';
import { finalize } from 'rxjs/operators';
import { UserFormComponent } from './user-form/user-form.component';
import { ChangePasswordComponent } from '../../authentication/change-password/change-password.component';
import { ChangeUsernameComponent } from '../../authentication/change-username/change-username.component';
import { ChangeMerchantTypeComponent } from '../../authentication/change-merchant-type/change-merchant-type.component';
import { ChangeEmailComponent } from '../../authentication/change-email/change-email.component';
import { ToastrService } from 'ngx-toastr';
import { USER_ROLES, USER_APPROVAL_STATUS } from '../../../enums';
import { Event } from '../../../interfaces';
import { HasRolePipe } from '../../../pipes/has-role.pipe';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.css']
})
export class UsersComponent extends BasicList {
  params = {
    where: { partnerId: null },
    include: { roles: 'role' },
    order: undefined
  };
  searchSettings = {
    searchFields: [],
    searchableFields: [
      { name: 'username', placeholder: 'Username' },
      { name: 'email', placeholder: 'Email' },
      { name: 'partnerId', placeholder: 'Account Id', defaultValue: { neq: null } },
    ],
    filters: {}
  };
  USER_ROLES: typeof USER_ROLES = USER_ROLES;
  USER_APPROVAL_STATUS: typeof USER_APPROVAL_STATUS = USER_APPROVAL_STATUS;
  host:string = LoopBackConfig.getPath();

  constructor(
    private userDataApi: UserDataApi,
    private ModalService: BsModalService,
    private hasRolePipe: HasRolePipe,
    private loopBackAuth: LoopBackAuth,
    private toast: ToastrService
  ) {
    super();
    this.settings = {
      createComponent: UserFormComponent,
      api: userDataApi,
      ModalService: ModalService,
      delete: {
        method: this.deleteMethod.bind(this),
        title: 'Confirm Deactivate',
        deleteText: 'Deactivate'
      }
    };
    if (this.getUser() && (
      this.hasRolePipe.transform(this.getUser().roles, USER_ROLES.ALTERNATIVE_CHANNEL) ||
      this.getUser() && this.hasRolePipe.transform(this.getUser().roles, USER_ROLES.BDM)
    )) {
      this.params.order = 'id DESC';
      this.params.where = { partnerId: { neq: null } };
    } else {
      this.params.order = 'partnerId ASC';
      this.searchSettings.searchableFields.pop();
    }
    this.searchSettings.searchFields.push(this.searchSettings.searchableFields[0]);
  }

  reactivate(id: string) {
    this.loading.reactivate = true;
    this.clearErrors();
    return this.userDataApi
      .patchAttributes(id, {
        active: true
      })
      .pipe(finalize(() => {
        this.loading.reactivate = false;
      }))
      .subscribe(
        (response: any) => {
          this.toast.success('User successfully reactivated', 'User');
          this.getList();
        },
        error => {
          this.errors.reactivate = error.message;
        }
      );
  }

  changeUsername(user: UserData) {
    const changeUserNameModal = this.ModalService
      .show(ChangeUsernameComponent, {
        initialState: {
          data: {
            id: user.id,
            username: user.username
          }
        }
      });
    const subscription = this.ModalService.onHidden
      .subscribe(() => {
        if (changeUserNameModal.content) {
          if (changeUserNameModal.content.result) {
            this.getList();
            this.toast.success('Successfully updated username!', 'Update Success');
          }
        }
        subscription.unsubscribe();
      });
  }

  changeEmail(user: UserData) {
    const changeEmailModal = this.ModalService
      .show(ChangeEmailComponent, {
        initialState: {
          data: {
            id: user.id,
            email: user.email
          }
        }
      });
    const subscription = this.ModalService.onHidden
      .subscribe(() => {
        if (changeEmailModal.content) {
          if (changeEmailModal.content.result) {
            this.getList();
            this.toast.success('Successfully updated email!', 'Update Success');
          }
        }
        subscription.unsubscribe();
      });
  }

  changePassword(user: UserData) {
    const changePasswordModal = this.ModalService
      .show(ChangePasswordComponent, {
        initialState: {
          data: { id: user.id }
        }
      });
    const subscription = this.ModalService.onHidden
      .subscribe(() => {
        if (changePasswordModal.content) {
          if (changePasswordModal.content.result) {
            this.toast.success('Successfully updated password!', 'Update Success');
          }
        }
        subscription.unsubscribe();
      });
  }

  changeMerchantType(user: UserData) {
    const userRole = (user.roles || [])
      .filter(role => {
        return parseInt((role.role.code || 0).toString()) >= 200 && parseInt((role.role.code || 0).toString()) < 300;
      })[0];
    const changeMerchantTypeModal = this.ModalService
      .show(ChangeMerchantTypeComponent, {
        initialState: {
          data: {
            id: user.id,
            merchantType: userRole.role.code,
            paymentType: userRole.paymentType,
            roleId: userRole.id
          }
        }
      });
    const subscription = this.ModalService.onHidden
      .subscribe(() => {
        if (changeMerchantTypeModal.content) {
          if (changeMerchantTypeModal.content.result) {
            this.toast.success('Successfully changed Merchant Type!', 'Update Success');
            this.getList();
          }
        }
        subscription.unsubscribe();
      });
  }

  deleteMethod(id: string) {
    return this.userDataApi
      .patchAttributes(id, {
        active: false
      });
  }

  modify(data) {
    const {
      id,
      firstName,
      lastName,
      middleName,
      extensionName,
      email,
      contactNumber
    } = data;
    this.edit({
      userClass: 'MAXICARE',
      data: {
        id,
        adminType: data && data.roles && data.roles[0] &&
          data.roles[0].principalType && data.roles[0].role &&
          data.roles[0].role.code,
        firstName,
        lastName,
        middleName,
        extensionName,
        email,
        contactNumber
      }
    });
  }

  approve(id) {
    this.loading.approve = true;
    this.clearErrors();
    this.userDataApi
      .approve(id)
      .pipe(finalize(() => {
        this.loading.approve = false;
      }))
      .subscribe(user => {
        this.getList();
      }, error => {
        this.errors.approve = error.message || 'Failed to approve user';
      });
  }

  reject(id) {
    this.loading.reject = true;
    this.clearErrors();
    this.userDataApi
      .patchAttributes(id, {
        approvalStatus: this.USER_APPROVAL_STATUS.DISAPPROVED
      })
      .pipe(finalize(() => {
        this.loading.reject = false;
      }))
      .subscribe(user => {
        this.getList();
      }, error => {
        this.errors.reject = error.message || 'Failed to reject user';
      });
  }

  review(id) {
    this.loading.review = true;
    this.clearErrors();
    this.userDataApi
      .patchAttributes(id, {
        approvalStatus: this.USER_APPROVAL_STATUS.PENDING
      })
      .pipe(finalize(() => {
        this.loading.review = false;
      }))
      .subscribe(user => {
        this.getList();
      }, error => {
        this.errors.review = error.message || 'Failed to review user';
      });
  }

  getUser() {
    return this.loopBackAuth.getToken() ? this.loopBackAuth.getCurrentUserData() : undefined;
  }

  eventListener(event: Event) {
    switch (event.event) {
      case 'delete:success':
        this.toast.success('User successfully deactivated', 'User');
        this.getList();
      break;
      case 'createModal:completed':
      case 'editModal:completed':
      case 'initialized':
        this.getList();
        break;
    }
  }
}
